/**
 * useAppliedTheme — Applies saved theme settings to CSS custom properties.
 *
 * Call once at app root level. Reads theme settings from the server
 * and applies them as CSS variables on :root so the entire UI responds.
 * Also dynamically loads Google Fonts if the selected font differs from default.
 *
 * Side effect: caches the shareDomain setting into localStorage so that
 * synchronous URL helpers (getPublishedBaseUrl, getClientBoardShareUrl)
 * can use it without an async call.
 *
 * Exposes a `refreshTheme()` helper via a custom event so that
 * the Settings page can trigger a re-fetch after saving.
 */

import { useEffect, useRef } from "react";
import { getSettings } from "../utils/settingsApi";
import { cacheShareDomain } from "../components/dashboard/dashboardConstants";

const GOOGLE_FONT_LINK_ID = "zap-theme-google-font";

/** Custom event name used to trigger a theme refresh from anywhere */
export const THEME_REFRESH_EVENT = "zap-theme-refresh";

function loadGoogleFont(fontFamily: string) {
  if (!fontFamily || fontFamily === "Heebo") return; // Heebo already loaded by default

  // Remove old link if present
  const old = document.getElementById(GOOGLE_FONT_LINK_ID);
  if (old) old.remove();

  const link = document.createElement("link");
  link.id = GOOGLE_FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontFamily)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

function fetchAndApply() {
  getSettings()
    .then((s) => {
      const root = document.documentElement;

      if (s.themePrimaryColor) root.style.setProperty("--primary", s.themePrimaryColor);
      if (s.themeAccentColor) root.style.setProperty("--accent", s.themeAccentColor);
      if (s.themeBackgroundColor) root.style.setProperty("--background", s.themeBackgroundColor);
      if (s.themeForegroundColor) root.style.setProperty("--foreground", s.themeForegroundColor);
      if (s.themeCardColor) root.style.setProperty("--card", s.themeCardColor);
      if (s.themeDestructiveColor) root.style.setProperty("--destructive", s.themeDestructiveColor);
      if (s.themeSidebarColor) root.style.setProperty("--sidebar", s.themeSidebarColor);
      if (s.themeBorderRadius != null) root.style.setProperty("--radius", s.themeBorderRadius + "px");
      if (s.themeFontSize != null) root.style.setProperty("--font-size", s.themeFontSize + "px");
      if (s.themeFontFamily) {
        loadGoogleFont(s.themeFontFamily);
        const fontValue = `'${s.themeFontFamily}', sans-serif`;
        root.style.setProperty("--font-family", fontValue);
        // Also apply directly to body as a safety net against Tailwind preflight overrides
        document.body.style.fontFamily = fontValue;
      }

      // Cache shareDomain for synchronous URL helpers
      cacheShareDomain(s.shareDomain);
    })
    .catch((err) => {
      /* Silently fail — theme defaults from CSS are already active */
      console.log("[useAppliedTheme] Could not load theme settings:", err);
    });
}

export function useAppliedTheme() {
  const applied = useRef(false);

  useEffect(() => {
    // Initial load — only once
    if (!applied.current) {
      applied.current = true;
      fetchAndApply();
    }

    // Listen for refresh events (e.g. after saving settings)
    const handler = () => fetchAndApply();
    window.addEventListener(THEME_REFRESH_EVENT, handler);
    return () => window.removeEventListener(THEME_REFRESH_EVENT, handler);
  }, []);
}

/** Call this from anywhere to force a theme refresh from the server */
export function refreshThemeFromServer() {
  window.dispatchEvent(new CustomEvent(THEME_REFRESH_EVENT));
}
